// @ts-check
import { find, propEq } from 'ramda'
import cloneDeep from 'lodash/cloneDeep'
import api from './api'
import { QUALIFICATION, DrugQualificationsEnum, LegacyDrugCertificationsEnum } from '../component-templates/certification-badge/constants'

export const QualificationsEnum = Object.freeze({
  "Background Check Level 1": "c2fe6196-2c2d-40a6-95d6-73c8e24b58d8",
  "Background Check Level 2": "50d7b749-5f48-4e0d-8089-6026ec7ad3dc",
  "Background Check Level 3": "109ca3c6-e815-4c52-9304-dd5b7465448c",
  "Background Check Level 4": "e61ad044-ba73-497f-b205-a4cf2ab3cd2d",
  "Background Check Level 5": "241d7b7a-aebc-4fb7-8224-13f999134a34",
  "Background Check Level 6": "c83c823b-37af-489a-9809-5dcef68d885d",
  "Background Check Level 7": "2be7e61c-f12b-4707-90e0-795c5542e243",
});

let candidateSources
let jobTypes
let skills
let skillCategories = []
let points
let ratingCategories
let regions
let qualifications

/**
 * @param {string} isoString - ISO 8601 formatted date string (e.g., "2024-05-15T12:00:00Z")
 * @returns {{year: number, month: number, day: number}}
 */
export function getDateFromISOString (isoString) {
  const dates = isoString.split('T')[0].split('-');
  return {
    year: parseInt(dates[0]),
    month: parseInt(dates[1]),
    day: parseInt(dates[2]),
  }
}

/**
 * @typedef {Object} Qualification
 * @property {string} qualificationId
 * @property {string} name
 */

/**
 * @typedef {Object} Certification
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string} badge_icon
 * @property {null} url
 */

/**
 * @typedef {Object} QualificationWithBadge
 * @property {string} badge_icon
 * @property {string} name
 * @property {string} qualificationId
 */

/**
 * @typedef {Object} ReferenceModule
 * @property {() => QualificationWithBadge[]} getQualifications
 * @property {() => Certification[]} getCertifications
 */

/**
 * @param {Object} $http - Angular $http service
 * @returns {ReferenceModule}
 */
const reference = ($http) => {
  'ngInject'

  const getCandidateSources = () => {
    if (candidateSources) {
      return Promise.resolve(candidateSources)
    }

    return $http({
      url: `${api.getBase()}/reference/candidateSources?is_active=1`,
      method: 'GET'
    })
    .then(result => {
      candidateSources = result.data
      return candidateSources
    })
  }

    /**
   * Get all qualifications that a worker may be assigned
   * @returns {Certification[]}
   */
  const getCertifications = () => {
    return $http({
      url: `${api.getBase()}/reference/certifications`,
      method: 'GET'
    })
    .then(result => {
      /**
       * @type {Certification[]}
       */
      const data = result.data;

      const legacyDrugScreenIds = new Set(Object.values(LegacyDrugCertificationsEnum));

      // Remove drug screen certifications because they have been migrated over to qualifications
      const filteredData = data.filter(cert => {
        return !legacyDrugScreenIds.has(cert.id.toString())
      })

      return filteredData
    })
  }


  const getJobTypes = () => {
    if (jobTypes) {
      return Promise.resolve(jobTypes)
    }

    return $http({
      method: 'GET',
      url: `${api.getBase()}/job/getTypes` //  reusing existing endpoint, ideally should be /reference/jobTypes
    })
    .then(result => {
      jobTypes = result.data
      return jobTypes
    },
      err => { throw err }
    )
  }

  const getSkills = () => {
    if (skills) {
      return Promise.resolve(skills)
    }

    return $http({
      url: `${api.getBase()}/reference/skills`,
      method: 'GET'
    })
    .then(result => {
      skills = result['data']
      skills.map(skill => {
        if (!find(propEq('id', skill.job_type_id), skillCategories)) {
          skillCategories.push({id: skill.job_type_id, name: skill.job_type_name})
        }
      })
      return skills
    })
  }

  /**
   * Get all qualifications that a worker may be assigned
   * @returns {QualificationWithBadge[]}
   */
  const getQualifications = () => {
    if (qualifications) {
      return cloneDeep(qualifications)
    }

    return $http({
      method: 'GET',
      url: `${api.getBaseCMOS()}/qualifications`
    })
    .then(result => {
      /**
       * @type {{qualifications: Qualification[]}}
       */
      const data = result.data;

      const backgroundCheckIds = new Set(Object.values(QualificationsEnum));
      const drugScreenIds = new Set(Object.values(DrugQualificationsEnum));

      const backgroundQualifications = data.qualifications.filter((qualification) => {
        return backgroundCheckIds.has(qualification.qualificationId)
      })

      const drugQualifications =  data.qualifications.filter((qualification) => {
        return drugScreenIds.has(qualification.qualificationId)
      })

      backgroundQualifications.forEach((qualification) => {
        qualification.badge_icon = QUALIFICATION.ICON_URLS.BACKGROUND_CHECK
      })

      drugQualifications.forEach((qualification) => {
        qualification.badge_icon = QUALIFICATION.ICON_URLS.DRUG_SCREEN
      })

      return cloneDeep([...backgroundQualifications, ...drugQualifications])
    })
  }

  const getSkillCategories = () => {
    if (skillCategories) {
      return Promise.resolve(skillCategories)
    } else {
      return getSkills()
      .then(() => {
        return skillCategories
      })
    }
  }

  const getPoints = () => {
    if (points) {
      return Promise.resolve(points)
    }

    return $http({
      url: `${api.getBase()}/reference/points`,
      method: 'GET'
    })
    .then(result => {
      points = result['data']
      return points
    })
  }

  const getRatingCategories = () => {
    if (ratingCategories) {
      return Promise.resolve(ratingCategories)
    }

    return $http({
      url: `${api.getBase()}/reference/ratingCategories`,
      method: 'GET'
    })
    .then(result => {
      ratingCategories = result['data']
      return ratingCategories
    })
  }

  const getRegions = () => {
    if (regions) {
      return Promise.resolve(regions)
    }

    return $http({
      url: `${api.getBase()}/regions/all`,
      method: 'GET'
    })
    .then(result => {
      regions = result['data']
      return regions
    })
  }

  return {
    getCandidateSources,
    getCertifications,
    getJobTypes,
    getSkills,
    getSkillCategories,
    getPoints,
    getRatingCategories,
    getRegions,
    getQualifications,
  }
}

export default reference
